if (!customElements.get('volume-tiers')) {
  customElements.define(
    'volume-tiers',
    class VolumeTiers extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.price = parseInt(this.dataset.price);
        this.tiers = Array.from(this.querySelectorAll('.volume-tiers__tier'));
        this.onQuantityInput = this.onQuantityInput.bind(this);

        this.tiers.forEach((tier) => tier.addEventListener('click', this.onTierClick.bind(this)));
      }

      connectedCallback() {
        // The quantity input can be re-rendered by product-info.js, so listen at the document level.
        document.addEventListener('input', this.onQuantityInput);
        document.addEventListener('change', this.onQuantityInput);
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.update());
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId || !event.data.variant) return;
          this.price = event.data.variant.price;
          this.update();
        });
        this.applyQuantityFromUrl();
        this.update();
      }

      // Links like /products/handle?quantity=35 (e.g. from the Bulk savings section) preselect a quantity.
      applyQuantityFromUrl() {
        const input = this.input;
        const quantity = parseInt(new URLSearchParams(window.location.search).get('quantity'));
        if (!input || !(quantity > 0)) return;

        input.value = input.max ? Math.min(quantity, parseInt(input.max)) : quantity;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      disconnectedCallback() {
        document.removeEventListener('input', this.onQuantityInput);
        document.removeEventListener('change', this.onQuantityInput);
        this.quantityUpdateUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
      }

      get input() {
        return document.getElementById(`Quantity-${this.sectionId}`);
      }

      onQuantityInput(event) {
        if (event.target === this.input) this.update();
      }

      onTierClick(event) {
        const input = this.input;
        if (!input) return;

        const tier = event.currentTarget;
        let quantity = Math.max(parseInt(tier.dataset.min), parseInt(input.min) || 1);
        if (input.max) quantity = Math.min(quantity, parseInt(input.max));

        input.value = quantity;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      unitPrice(discount) {
        return Math.round((this.price * (100 - discount)) / 100);
      }

      update() {
        const input = this.input;
        const quantity = input ? parseInt(input.value) || 0 : 0;
        const tierIndex = this.tiers.findLastIndex((tier) => quantity >= parseInt(tier.dataset.min));
        const activeTier = this.tiers[tierIndex];
        const nextTier = this.tiers[tierIndex + 1];

        this.tiers.forEach((tier) => {
          tier.setAttribute('aria-pressed', tier === activeTier);
          tier.querySelector('.volume-tiers__unit-price').textContent = this.formatMoney(
            this.unitPrice(parseInt(tier.dataset.discount))
          );
        });

        const discount = activeTier ? parseInt(activeTier.dataset.discount) : 0;

        const total = document.getElementById(`VolumeTiersTotal-${this.sectionId}`);
        if (total) total.textContent = this.formatMoney(Math.round((quantity * this.price * (100 - discount)) / 100));

        const hint = document.getElementById(`VolumeTiersHint-${this.sectionId}`);
        if (!hint) return;
        if (nextTier) {
          hint.textContent = this.dataset.hintNext
            .replace('[quantity]', parseInt(nextTier.dataset.min) - quantity)
            .replace('[price]', this.formatMoney(this.unitPrice(parseInt(nextTier.dataset.discount))));
        } else if (discount > 0) {
          hint.textContent = this.dataset.hintMax.replace('[percent]', discount);
        } else {
          hint.textContent = '';
        }
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const formatNumber = (decimals, thousands, decimal) => {
          const [integer, fraction] = (cents / 100).toFixed(decimals).split('.');
          return integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
        };

        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, placeholder) => {
          switch (placeholder) {
            case 'amount_no_decimals':
              return formatNumber(0, ',', '.');
            case 'amount_with_comma_separator':
              return formatNumber(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return formatNumber(0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return formatNumber(2, "'", '.');
            default:
              return formatNumber(2, ',', '.');
          }
        });
      }
    }
  );
}
